import { History } from 'history'
import { createContext } from 'react'
import {
  AccountDetailsState,
  AccountNotesState,
  AccountOwnerState,
  AccountSearchState,
  ActivitiesState,
  BalanceState,
  CorrespondentState,
  TradingRestrictionsState,
  AccountPositionsState,
  MarginCallsState,
  MarginsState,
  AuthorizedTradersState,
  InvestmentProfileState,
  DocumentsState,
  EPreferencesState,
  AmountAvailableState,
  InterestedPartiesState,
  SecuremailState,
  DocumentsType,
} from 'types'

export const INITIAL_ACCOUNT_DETAILS_STATE: AccountDetailsState = {
  accountDetails: undefined,
  loadingResult: false,
}

export const INITIAL_ACCOUNT_OWNERS_STATE: AccountOwnerState = {
  accountOwners: [],
  loadingAccountOwners: false,
}

export const INITIAL_ACCOUNT_SEARCH_STATE: AccountSearchState = {
  accountResults: [],
  loadingResults: false,
  errorMessage: undefined,
  searchTerm: '',
}

export const INITIAL_ACTIVITIES_STATE: ActivitiesState = {
  activities: undefined,
  loadingActivities: false,
  processDate: undefined,
}

export const INITIAL_BALANCE_STATE: BalanceState = {
  balances: {},
  loadingBalances: false,
}

export const INITIAL_AUTHORIZED_TRADERS_STATE: AuthorizedTradersState = {
  authorizedTraders: [],
  loadingAuthorizedTraders: false,
}

export const INITIAL_INVESTMENT_PROFILE_STATE: InvestmentProfileState = {
  investmentProfile: undefined,
  loadingInvestmentProfile: false,
}

export const INITIAL_E_PREFERENCES_STATE: EPreferencesState = {
  ePreferences: undefined,
  loadingEPreferences: false,
}

const EMPTY_CORRESPONDENT = {
  code: '',
  name: '',
}

export const INITIAL_INTERESTED_PARTIES_STATE: InterestedPartiesState = {
  interestedParties: undefined,
  loadingInterestedParties: false,
}

const EMPTY_MARGINS = {
  sma: 0,
  dayTradeBuyingPower: 0,
  overnightBuyingPower: 0,
  marginEquity: 0,
  marginExcess: 0,
  marginRequirement: 0,
  cashAvailableToWithdraw: 0,
  fullyPaidUnsettledFunds: 0,
  cashExcessEquity: 0,
  netLiquidity: 0,
  totalMarketValue: 0,
  liquidatingEquity: 0,
  marginEquityPercent: 0,
  committedSma: 0,
  regTBuyingPower: 0,
  fdicBalance: 0,
  concentratedMaintExcess: 0,
  pendingInterest: 0,
  pendingDividend: 0,
  processDate: '',
}

export const INITIAL_MARGIN_CALL_STATE: MarginCallsState = {
  marginCalls: [],
  loadingMarginCalls: false,
}

export const INITIAL_MARGINS_STATE: MarginsState = {
  margins: EMPTY_MARGINS,
  loadingMargins: false,
}

export const INITIAL_CORRESPONDENT_STATE: CorrespondentState = {
  correspondent: EMPTY_CORRESPONDENT,
  loadingResult: false,
}

export const INITIAL_ACCOUNT_NOTES_STATE: AccountNotesState = {
  accountNotes: [],
  loadingAccountNotes: false,
}

export const INITIAL_TRADING_RESTRICTIONS_STATE: TradingRestrictionsState = {
  tradingRestrictions: undefined,
  loadingTradingRestrictions: false,
}

export const INITIAL_ACCOUNT_POSITIONS_STATE: AccountPositionsState = {
  accountPositions: [],
  loadingPositions: false,
}

export const INITIAL_DOCUMENTS_STATE: DocumentsState = {
  documents: [],
  loadingDocuments: false,
}

export const INITIAL_AMOUNT_AVAILABLE_STATE: AmountAvailableState = {
  amountAvailable: undefined,
  loadingAmountAvailable: false,
}

export const INITIAL_SECUREMAIL_STATE: SecuremailState = {
  securemailLinks: undefined,
  loadingSecuremailLinks: false,
}

export interface SearchProps {
  accountDetailsState: AccountDetailsState
  accountNotesState: AccountNotesState
  accountOwnerState: AccountOwnerState
  accountPositionsState: AccountPositionsState
  accountSearchState: AccountSearchState
  activitiesState: ActivitiesState
  balanceState: BalanceState
  correspondentState: CorrespondentState
  marginCallsState: MarginCallsState
  marginsState: MarginsState
  tradingRestrictionsState: TradingRestrictionsState
  authorizedTradersState: AuthorizedTradersState
  investmentProfileState: InvestmentProfileState
  documentsState: DocumentsState
  ePreferencesState: EPreferencesState
  amountAvailableState: AmountAvailableState
  interestedPartiesState: InterestedPartiesState
  securemailState: SecuremailState
  getAccountDetails: (accountNumber: string) => void
  getAccountNotes: (accountNumber: string) => void
  getAccountOwners: (accountNumber: string) => void
  getAccountPositions: (accountNumber: string, processDate?: string) => void
  getActivities: (
    accountNumber: string,
    startDate: string,
    endDate: string
  ) => void
  getBalances: (accountNumber: string, processDate?: string) => void
  getCorrespondent: (accountNumber: string) => void
  getMarginCalls: (accountNumber: string) => void
  getMargins: (accountNumber: string) => void
  getTradingRestrictions: (accountNumber: string) => void
  getAuthorizedTraders: (accountNumber: string) => void
  getInvestmentProfile: (accountNumber: string) => void
  getDocuments: (accountNumber: string) => void
  getEPreferences: (accountNumber: string) => void
  getInterestedParties: (accountNumber: string) => void
  searchAccounts: (searchTerm: string, history: History) => void
  getAmountAvailable: (accountNumber: string) => void
  getSecuremailLinks: (
    accountNumber: string,
    documentType: DocumentsType
  ) => void
}

export const SearchContext = createContext<SearchProps>({
  accountDetailsState: INITIAL_ACCOUNT_DETAILS_STATE,
  accountNotesState: INITIAL_ACCOUNT_NOTES_STATE,
  accountOwnerState: INITIAL_ACCOUNT_OWNERS_STATE,
  accountPositionsState: INITIAL_ACCOUNT_POSITIONS_STATE,
  accountSearchState: INITIAL_ACCOUNT_SEARCH_STATE,
  activitiesState: INITIAL_ACTIVITIES_STATE,
  balanceState: INITIAL_BALANCE_STATE,
  correspondentState: INITIAL_CORRESPONDENT_STATE,
  marginCallsState: INITIAL_MARGIN_CALL_STATE,
  marginsState: INITIAL_MARGINS_STATE,
  tradingRestrictionsState: INITIAL_TRADING_RESTRICTIONS_STATE,
  authorizedTradersState: INITIAL_AUTHORIZED_TRADERS_STATE,
  investmentProfileState: INITIAL_INVESTMENT_PROFILE_STATE,
  documentsState: INITIAL_DOCUMENTS_STATE,
  ePreferencesState: INITIAL_E_PREFERENCES_STATE,
  amountAvailableState: INITIAL_AMOUNT_AVAILABLE_STATE,
  interestedPartiesState: INITIAL_INTERESTED_PARTIES_STATE,
  securemailState: INITIAL_SECUREMAIL_STATE,
  getAccountDetails: () => {},
  getAccountNotes: () => {},
  getAccountOwners: () => {},
  getAccountPositions: () => {},
  getActivities: () => {},
  getBalances: () => {},
  getCorrespondent: () => {},
  getMarginCalls: () => {},
  getMargins: () => {},
  getTradingRestrictions: () => {},
  getAuthorizedTraders: () => {},
  getInvestmentProfile: () => {},
  getDocuments: () => {},
  getEPreferences: () => {},
  getInterestedParties: () => {},
  searchAccounts: () => {},
  getAmountAvailable: () => {},
  getSecuremailLinks: () => {},
})
